import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Theme modes: light, dark, glass
const THEME_MODES = ['light', 'dark', 'glass'];

const DEFAULT_BRANDING = {
  colors: {
    primary: '#2563EB',
    secondary: '#111827',
    accent: '#F59E0B',
    background: '#FFFFFF',
    surface: '#F9FAFB',
    text: '#111827',
    mutedText: '#6B7280',
    border: '#E5E7EB'
  },
  fonts: {
    headingFont: 'Poppins',
    bodyFont: 'Inter'
  },
  assets: {
    logoUrl: null,
    logoAlt: 'Graphikpro',
    faviconUrl: null
  },
  accentColor: '#8B5CF6',
  pageTitle: 'Graphikpro'
};

const ThemeContext = createContext({
  theme: DEFAULT_BRANDING,
  themeMode: 'light',
  setThemeMode: () => {},
  animationsEnabled: false,
  setAnimationsEnabled: () => {},
  loading: true
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(DEFAULT_BRANDING);
  const [themeMode, setThemeModeState] = useState('light');
  const [animationsEnabled, setAnimationsEnabledState] = useState(() => {
    const saved = localStorage.getItem('graphikpro-animations');
    return saved === 'true';
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load saved theme mode from localStorage on mount
    const savedMode = localStorage.getItem('graphikpro-theme-mode');
    if (savedMode && THEME_MODES.includes(savedMode)) {
      setThemeModeState(savedMode);
      applyThemeMode(savedMode);
    }
    
    // Apply animations class on mount
    applyAnimationsClass(animationsEnabled);
    
    // Load branding from API
    const loadBranding = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${API_URL}/api/settings`);
        if (response.ok) {
          const data = await response.json();
          const mergedTheme = {
            colors: { ...DEFAULT_BRANDING.colors, ...data.colors },
            fonts: { ...DEFAULT_BRANDING.fonts, ...data.fonts },
            assets: { ...DEFAULT_BRANDING.assets, ...data.assets },
            accentColor: data.accentColor || DEFAULT_BRANDING.accentColor,
            pageTitle: data.pageTitle || DEFAULT_BRANDING.pageTitle
          };
          setTheme(mergedTheme);
          applyBranding(mergedTheme);
        } else {
          applyBranding(DEFAULT_BRANDING);
        }
      } catch (error) {
        console.error('Failed to load theme:', error);
        applyBranding(DEFAULT_BRANDING);
      } finally {
        setLoading(false);
      }
    };
    
    loadBranding();
  }, []);

  const setThemeMode = useCallback((mode) => {
    if (!THEME_MODES.includes(mode)) return;
    setThemeModeState(mode);
    localStorage.setItem('graphikpro-theme-mode', mode);
    applyThemeMode(mode);
  }, []);

  const setAnimationsEnabled = useCallback((enabled) => {
    setAnimationsEnabledState(enabled);
    localStorage.setItem('graphikpro-animations', String(enabled));
    applyAnimationsClass(enabled);
  }, []);

  const applyAnimationsClass = (enabled) => {
    const html = document.documentElement;
    if (enabled) {
      html.classList.remove('no-animations');
    } else {
      html.classList.add('no-animations');
    }
  };

  const applyThemeMode = (mode) => {
    const html = document.documentElement;
    
    // Remove all theme classes first
    html.classList.remove('light', 'dark', 'glass');
    
    // Apply the selected mode
    html.classList.add(mode);
    
    // For dark mode, we need to apply the dark class specifically
    if (mode === 'dark') {
      html.classList.add('dark');
    }
  };

  const applyBranding = (themeData) => {
    const root = document.documentElement;
    
    // Apply color CSS variables
    if (themeData.colors) {
      Object.entries(themeData.colors).forEach(([key, value]) => {
        const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        root.style.setProperty(`--theme-${cssKey}`, value);
      });
    }
    
    // Apply accent color CSS variable
    if (themeData.accentColor) {
      root.style.setProperty('--accent-color', themeData.accentColor);
    }
    
    // Apply page title
    if (themeData.pageTitle) {
      document.title = themeData.pageTitle;
    }
    
    // Apply font CSS variables
    if (themeData.fonts) {
      root.style.setProperty('--font-heading', `"${themeData.fonts.headingFont}", sans-serif`);
      root.style.setProperty('--font-body', `"${themeData.fonts.bodyFont}", sans-serif`);
      loadGoogleFonts(themeData.fonts);
    }
    
    // Update favicon if provided
    if (themeData.assets?.faviconUrl) {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const faviconLink = document.querySelector("link[rel*='icon']") || document.createElement('link');
      faviconLink.type = 'image/x-icon';
      faviconLink.rel = 'shortcut icon';
      faviconLink.href = `${API_URL}${themeData.assets.faviconUrl}`;
      document.getElementsByTagName('head')[0].appendChild(faviconLink);
    }
  };

  const loadGoogleFonts = (fonts) => {
    const fontsToLoad = new Set();
    if (fonts.headingFont) fontsToLoad.add(fonts.headingFont);
    if (fonts.bodyFont) fontsToLoad.add(fonts.bodyFont);
    
    if (fontsToLoad.size === 0) return;
    
    const existingLink = document.getElementById('google-fonts-dynamic');
    if (existingLink) existingLink.remove();
    
    const fontFamilies = Array.from(fontsToLoad)
      .map(f => f.replace(/ /g, '+') + ':wght@300;400;500;600;700')
      .join('&family=');
    
    const link = document.createElement('link');
    link.id = 'google-fonts-dynamic';
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fontFamilies}&display=swap`;
    document.head.appendChild(link);
  };

  return (
    <ThemeContext.Provider value={{ theme, themeMode, setThemeMode, animationsEnabled, setAnimationsEnabled, loading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
